import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { PickupSlotCode } from '../../services/pickupSlots.api';
import { closeSlot } from '../../services/pickupSlots.api';

interface Props {
  slotCode: PickupSlotCode;
  operationalDate: string;
  pendingCount: number; // rows still slot_assigned/route_included/route_appended
  onClose: () => void;
}

/**
 * Confirms a manual slot close. Consequence is spelled out: unreached pickups
 * roll to the next slot (S5 → next day) and merchants get notified by core.
 */
export default function CloseSlotModal({ slotCode, operationalDate, pendingCount, onClose }: Props) {
  const [error, setError] = useState<string | null>(null);
  const queryClient = useQueryClient();

  const mutation = useMutation({
    mutationFn: () => closeSlot(slotCode, operationalDate),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['pickupSlotBoard'] });
      onClose();
    },
    onError: (err: { response?: { data?: { error?: string } } }) =>
      setError(err.response?.data?.error ?? 'Failed to close the slot'),
  });

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="w-full max-w-md rounded-lg bg-white p-5 shadow-xl">
        <h2 className="text-lg font-semibold text-gray-900">Close slot {slotCode} now?</h2>
        <p className="mt-2 text-sm text-gray-600">
          {pendingCount} unreached pickup{pendingCount === 1 ? '' : 's'} will be{' '}
          {slotCode === 'S5' ? 'moved to the next operational day' : 'rolled over to the next slot'} and the
          merchants will be notified. This cannot be undone — the slot closes once.
        </p>
        {error && <p className="mt-2 text-sm text-red-600">{error}</p>}
        <div className="mt-4 flex justify-end gap-2">
          <button onClick={onClose} className="rounded-md border border-gray-300 px-3 py-1.5 text-sm">
            Cancel
          </button>
          <button
            onClick={() => mutation.mutate()}
            disabled={mutation.isPending}
            className="rounded-md bg-amber-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-amber-700 disabled:opacity-50"
          >
            {mutation.isPending ? 'Closing…' : 'Close slot'}
          </button>
        </div>
      </div>
    </div>
  );
}
